import { Component } from '@theme/component';

/**
 * Matcha universe switcher.
 *
 * Owns one piece of state for the whole matcha page: which of the two universes is showing,
 * `traditional` or `mixes`. It writes that state as a `data-matcha-universe` attribute on
 * `#MainContent`, the only DOM ancestor every section of a template shares, and every section
 * that changes between the two reads the attribute from its own stylesheet. Sections never
 * talk to this component and never know a switcher exists.
 *
 * The markup underneath is a pair of radio inputs, so keyboard support, focus handling and the
 * roving selection all come from the browser. This component adds three things the radios
 * cannot do on their own: mirror the choice onto the shared ancestor, keep the URL in step so a
 * link can open the page straight into `mixes`, and keep several switchers in agreement.
 *
 * The matcha page carries two of them, both published by dt-matcha-hero: the one on the seam
 * between the hero panels, and a floating copy that takes over once the first has scrolled away.
 * An instance marked `data-sticky` handles nothing but its own visibility — it watches the
 * unmarked one and the footer, and shows itself only while neither is on screen. Everything else,
 * state and synchronisation included, is the same code path for both.
 *
 * Switching happens on click only. There is no timer and no auto-rotation: the control changes
 * the whole page, and a page that rearranged itself unprompted would be hostile to anyone
 * reading it with a screen reader or coming back to a tab they left open.
 */

const ATTRIBUTE = 'data-matcha-universe';
const ANIMATE = 'data-matcha-animate';
const PARAM = 'universe';
const UNIVERSES = ['traditional', 'mixes'];
const DEFAULT_UNIVERSE = 'traditional';
const EVENT = 'dt:matcha-universe';

/**
 * @typedef {object} Refs
 * @property {HTMLFieldSetElement} group
 *
 * @extends {Component<Refs>}
 */
export class DtMatchaSwitcher extends Component {
  requiredRefs = ['group'];

  /** @type {HTMLElement | null} */
  #host = null;

  /** @type {HTMLInputElement[]} */
  #inputs = [];

  /** @type {IntersectionObserver | null} */
  #anchorObserver = null;

  /** @type {IntersectionObserver | null} */
  #footerObserver = null;

  /**
   * The two answers the floating copy is the sum of. It shows itself only while the switcher it
   * stands in for has left the screen and the footer has not yet arrived on it.
   *
   * The anchor starts on screen and the footer off it, which is the top of the page: the pill is
   * hidden there, which is where it has to be, and neither observer has had to fire yet for that
   * to be true.
   */
  #anchorOnScreen = true;

  #footerOnScreen = false;

  connectedCallback() {
    super.connectedCallback();

    this.#host = document.getElementById('MainContent') ?? this.closest('main');
    this.#inputs = /** @type {HTMLInputElement[]} */ ([
      ...this.querySelectorAll('input[type="radio"]'),
    ]);

    this.addEventListener('change', this.#onChange);
    document.addEventListener(EVENT, this.#onExternalChange);

    if (this.hasAttribute('data-sticky')) this.#watchAnchor();

    // The inline script in the section already set the attribute before first paint so the page
    // never flashes the wrong universe. Read it back rather than reapplying a guess.
    const current = this.#host?.getAttribute(ATTRIBUTE);
    this.#reflect(UNIVERSES.includes(current ?? '') ? /** @type {string} */ (current) : DEFAULT_UNIVERSE);

    // Arm the transitions, once the first frame is behind us. The stylesheet hangs everything that
    // animates on this attribute, so a page load paints its universe outright and only a switch
    // fades. Two frames rather than one: the rule has to be in force before the state it animates
    // changes, or the first click would resolve both at once and skip its own transition.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.#host?.setAttribute(ANIMATE, ''));
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.removeEventListener('change', this.#onChange);
    document.removeEventListener(EVENT, this.#onExternalChange);
    this.#anchorObserver?.disconnect();
    this.#anchorObserver = null;
    this.#footerObserver?.disconnect();
    this.#footerObserver = null;
  }

  /**
   * Show this copy only while the switcher it stands in for is off screen.
   *
   * An observer rather than a scroll listener: the question is whether one element is in view, which
   * is the one question `IntersectionObserver` answers natively, off the main thread and without a
   * measurement on every frame. A scroll handler would have to read the anchor's box each time and
   * would still be answering it late.
   *
   * The default threshold of 0 is what is wanted here — the anchor counts as visible while any part
   * of it remains, so the two never overlap on screen and never leave a gap between them. The
   * observer fires once on observe, so the opening state is settled without a separate first pass.
   *
   * With no anchor to watch there is nothing to defer to, so the pill stays out of the anchor's way
   * for good and only the footer can take it off screen. That case does not arise on the matcha
   * page, where both instances come from the same section, but a copy dropped anywhere else should
   * still work rather than stay invisible with no way to know why.
   */
  #watchAnchor() {
    const anchor = document.querySelector('dt-matcha-switcher-component:not([data-sticky])');

    if (anchor) {
      this.#anchorObserver = new IntersectionObserver(([entry]) => {
        this.#anchorOnScreen = entry?.isIntersecting ?? false;
        this.#render();
      });

      this.#anchorObserver.observe(anchor);
    } else {
      this.#anchorOnScreen = false;
      this.#render();
    }

    this.#watchFooter();
  }

  /**
   * Take the pill away once the footer arrives.
   *
   * The same instrument as the anchor, for the same reason, and no root of its own. Above 990 the
   * page scrolls inside `.page-wrapper` rather than in the document, which is exactly the case a
   * hand-rolled scroll handler gets wrong; an observer left on the default root does not need to
   * know, because intersection is computed through every clipping ancestor before the viewport has
   * its say.
   *
   * Threshold 0 again: the pill goes as the first pixel of the footer appears. Anything higher
   * would keep it hovering over the footer for a stretch.
   *
   * The class first, the tag as a fallback, and nothing at all if the page has neither: a page with
   * no footer leaves the pill governed by the anchor alone, rather than hiding a control on the
   * strength of a selector that found nothing.
   */
  #watchFooter() {
    const footer = document.querySelector('.dt-footer') ?? document.querySelector('footer');

    if (!footer) return;

    this.#footerObserver = new IntersectionObserver(([entry]) => {
      this.#footerOnScreen = entry?.isIntersecting ?? false;
      this.#render();
    });

    this.#footerObserver.observe(footer);
  }

  /**
   * The one place `data-visible` is written, so the two observers cannot argue about it. The
   * stylesheet fades and slides off this attribute, so a change here is a transition and not a
   * jump.
   */
  #render() {
    this.toggleAttribute('data-visible', !this.#anchorOnScreen && !this.#footerOnScreen);
  }

  /**
   * Both handlers are fields holding arrow functions rather than methods. A private method cannot
   * be reassigned, so binding one to the instance throws on the spot and the listeners below are
   * never attached. An arrow field closes over the instance on construction and needs no bind.
   *
   * @param {Event} event
   */
  #onChange = (event) => {
    const input = /** @type {HTMLInputElement} */ (event.target);
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
    if (!UNIVERSES.includes(input.value)) return;

    this.#apply(input.value);
    document.dispatchEvent(new CustomEvent(EVENT, { detail: { universe: input.value, source: this } }));
  };

  /** @param {Event} event */
  #onExternalChange = (event) => {
    const { universe, source } = /** @type {CustomEvent} */ (event).detail ?? {};
    if (source === this || !UNIVERSES.includes(universe)) return;
    this.#reflect(universe);
  };

  /** @param {string} universe */
  #apply(universe) {
    this.#host?.setAttribute(ATTRIBUTE, universe);
    this.#writeUrl(universe);
  }

  /**
   * Keep the radios of this switcher in step with a state that was decided elsewhere: by the
   * inline script on load, by the URL, or by another switcher on the page.
   *
   * @param {string} universe
   */
  #reflect(universe) {
    this.#host?.setAttribute(ATTRIBUTE, universe);
    for (const input of this.#inputs) input.checked = input.value === universe;
  }

  /**
   * Write the universe into the query string so the choice survives a copied link, a refresh or
   * a menu entry pointing straight at the mixes side. `replaceState` rather than `pushState`:
   * flipping a control on the page is not a navigation, and stacking history entries would turn
   * the back button into an undo button for a toggle.
   *
   * @param {string} universe
   */
  #writeUrl(universe) {
    if (!window.history?.replaceState) return;

    const url = new URL(window.location.href);
    if (universe === DEFAULT_UNIVERSE) url.searchParams.delete(PARAM);
    else url.searchParams.set(PARAM, universe);

    window.history.replaceState(window.history.state, '', url);
  }
}

if (!customElements.get('dt-matcha-switcher-component')) {
  customElements.define('dt-matcha-switcher-component', DtMatchaSwitcher);
}

/**
 * Theme editor only.
 *
 * A section carrying `data-matcha-only` is display:none in the other universe. Picking it in the
 * editor sidebar would then scroll to nothing, and a section that shows nothing when you select it
 * reads as a section that is broken. Selecting it switches the page to the universe it lives in,
 * which is both the fix and the honest answer to what the merchant just asked to see.
 *
 * Shopify only fires this event inside the editor, and the listener is only attached there.
 */
if (window.Shopify?.designMode) {
  document.addEventListener('shopify:section:select', (event) => {
    const wrapper = /** @type {HTMLElement | null} */ (event.target);
    const universe = wrapper?.querySelector(':scope > [data-matcha-only]')?.getAttribute('data-matcha-only');
    if (!UNIVERSES.includes(universe ?? '')) return;

    document.dispatchEvent(new CustomEvent(EVENT, { detail: { universe } }));
  });
}
